import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Grid, 
  TextField, 
  Button, 
  CircularProgress,
  Chip,
  Alert,
  Collapse,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import UndoIcon from '@mui/icons-material/Undo';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import enhancedFinancialDataService from '../services/EnhancedFinancialDataService';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell, Treemap, Legend, LineChart, Line } from 'recharts';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { useTheme } from '@mui/material/styles';
import { useAuth0 } from '@auth0/auth0-react';
import { setLocalStorageWithCleanup } from '../utils/cacheManager';

// Empty string for same-origin API calls (nginx proxies /api/)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';

const MAX_SEARCH_RESULTS = 50;
const MAX_COMPARE_TICKERS = 15;
const ACTIVE_WATCHLIST_STORAGE_KEY = 'watchlists.active.v1';
const ACTIVE_WATCHLIST_SCOPE_STORAGE_KEY = 'watchlists.active.scope.v1';
const WATCHLIST_STATE_CACHE_PREFIX = 'watchlists.compare.state.v1';
const COMPARE_RECENT_TICKERS_STORAGE_KEY = 'comparison.recent.tickers.v1';
const FINANCIAL_FONT_STACK = '"Inter", "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif';
const FINANCIAL_NUMERIC_FONT_STACK = '"Inter", "SF Pro Text", "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif';
const TREND_LOOKBACK_OPTIONS = [
  { value: 1, label: '1Y' },
  { value: 2, label: '2Y' },
  { value: 5, label: '5Y' },
  { value: 10, label: '10Y' },
  { value: 30, label: 'Max' },
];
const TREND_SERIES_COLORS = ['#2563eb', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#84cc16', '#f97316'];
const COMPARISON_SECTION_SX = {
  width: '100%',
  maxWidth: 'none',
  mx: { lg: -1.5 },
};

// Pre-configured filter for performance - matches ticker or name, limits results
const tickerFilterOptions = createFilterOptions({
  limit: MAX_SEARCH_RESULTS,
  stringify: (option) => `${option?.ticker || ''} ${option?.name || ''}`,
});

const normalizeTickers = (rawTickers) => {
  if (!Array.isArray(rawTickers)) return [];
  const normalized = rawTickers
    .map((ticker) => (typeof ticker === 'string' ? ticker.trim().toUpperCase() : ''))
    .filter(Boolean);
  return [...new Set(normalized)];
};

const clampComparisonTickers = (rawTickers) => normalizeTickers(rawTickers).slice(0, MAX_COMPARE_TICKERS);

const delay = (ms) => new Promise((resolve) => {
  window.setTimeout(resolve, ms);
});

const getWatchlistStateCacheKey = (identity) => {
  const normalizedIdentity = String(identity || '').trim().toLowerCase();
  return normalizedIdentity ? `${WATCHLIST_STATE_CACHE_PREFIX}:${normalizedIdentity}` : '';
};

const readWatchlistStateCache = (cacheKey) => {
  if (!cacheKey) return null;
  try {
    const raw = localStorage.getItem(cacheKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeWatchlistStateCache = (cacheKey, state) => {
  if (!cacheKey) return;
  try {
    setLocalStorageWithCleanup(cacheKey, JSON.stringify(state), { targetRemovals: 12 });
  } catch {
    // Ignore storage failures.
  }
};

const readRecentCompareTickers = () => {
  try {
    const raw = localStorage.getItem(COMPARE_RECENT_TICKERS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? clampComparisonTickers(parsed) : [];
  } catch {
    return [];
  }
};

const writeRecentCompareTickers = (tickers) => {
  try {
    setLocalStorageWithCleanup(
      COMPARE_RECENT_TICKERS_STORAGE_KEY,
      JSON.stringify(clampComparisonTickers(tickers)),
      { targetRemovals: 10 }
    );
  } catch {
    // Ignore storage write failures.
  }
};

const CompanyComparison = ({ initialTicker = 'AAPL' }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const runtimeEnv = window.__RUNTIME_ENV__ || {};
  const authAudience = String(runtimeEnv.AUTH0_AUDIENCE || process.env.REACT_APP_AUTH0_AUDIENCE || '').trim();

  const userId = isAuthenticated && user?.sub ? user.sub : 'anonymous';
  const userEmail = isAuthenticated && user?.email ? String(user.email).trim().toLowerCase() : '';
  const userDisplayName = isAuthenticated && user?.name ? String(user.name).trim() : '';
  const [authToken, setAuthToken] = useState('');
  const watchlistStateCacheKey = useMemo(
    () => getWatchlistStateCacheKey(userEmail || userId),
    [userEmail, userId]
  );
  const cachedWatchlistState = useMemo(
    () => readWatchlistStateCache(watchlistStateCacheKey),
    [watchlistStateCacheKey]
  );

  const getApiAccessToken = useCallback(async () => {
    const authorizationParams = {
      scope: 'openid profile email',
      ...(authAudience ? { audience: authAudience } : {}),
    };

    return String((await getAccessTokenSilently({ authorizationParams })) || '');
  }, [authAudience, getAccessTokenSilently]);

  useEffect(() => {
    let active = true;

    const loadToken = async () => {
      if (!isAuthenticated) {
        if (active) setAuthToken('');
        return;
      }

      try {
        const token = await getApiAccessToken();
        if (active) {
          setAuthToken(String(token || ''));
        }
      } catch (error) {
        if (active) {
          setAuthToken('');
        }
      }
    };

    loadToken();

    return () => {
      active = false;
    };
  }, [isAuthenticated, getApiAccessToken]);

  const authHeaders = useMemo(() => {
    const headers = { 'X-User-Id': userId };
    if (userEmail) {
      headers['X-User-Email'] = userEmail;
    }
    if (userDisplayName) {
      headers['X-User-Name'] = userDisplayName;
    }
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }
    return headers;
  }, [userId, userEmail, userDisplayName, authToken]);

  const buildRequestHeaders = useCallback(async () => {
    const headers = {
      'X-User-Id': userId,
    };

    if (userEmail) {
      headers['X-User-Email'] = userEmail;
    }
    if (userDisplayName) {
      headers['X-User-Name'] = userDisplayName;
    }

    let token = authToken;
    if (!token && isAuthenticated) {
      try {
        token = await getApiAccessToken();
        if (token) {
          setAuthToken(token);
        }
      } catch (error) {
        token = '';
      }
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }, [authToken, getApiAccessToken, isAuthenticated, userDisplayName, userEmail, userId]);

  // Currency symbols for display
  const currencySymbols = {
    USD: '$',
    JPY: '¥',
    EUR: '€',
    GBP: '£',
    CNY: '¥',
    TWD: 'NT$',
    KRW: '₩',
    INR: '₹',
    BRL: 'R$',
    CAD: 'C$',
    AUD: 'A$',
    CHF: 'Fr',
    RMB: '¥',
  };
  
  const getCurrencySymbol = (curr) => currencySymbols[curr] || curr || '$';

  const prefilledTickers = normalizeTickers(location.state?.prefillTickers);
  const [companies, setCompanies] = useState(
    prefilledTickers.length ? clampComparisonTickers(prefilledTickers) : [initialTicker]
  );
  const [newCompany, setNewCompany] = useState('');
  const [tickerPickerInput, setTickerPickerInput] = useState('');
  const [tickers, setTickers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTickers, setLoadingTickers] = useState(false);
  const [comparisonData, setComparisonData] = useState([]);
  const [error, setError] = useState(null);
  const [tickersError, setTickersError] = useState(null);
  const [watchlists, setWatchlists] = useState(() => (
    Array.isArray(cachedWatchlistState?.watchlists) ? cachedWatchlistState.watchlists : []
  ));
  const [watchlistsLoading, setWatchlistsLoading] = useState(false);
  const [watchlistsError, setWatchlistsError] = useState('');
  const [selectedWatchlistId, setSelectedWatchlistId] = useState(() => String(cachedWatchlistState?.selectedWatchlistId || ''));
  const [lastRemovedTicker, setLastRemovedTicker] = useState('');
  const [periodsExpanded, setPeriodsExpanded] = useState(false);
  const autoPrefillAppliedRef = useRef(false);
  // New state variables for period selection
  const [periodsByCompany, setPeriodsByCompany] = useState({});
  const [selectedPeriods, setSelectedPeriods] = useState({});
  const [overviewByTicker, setOverviewByTicker] = useState({});
  const [priceByTicker, setPriceByTicker] = useState({});
  const [currencyByTicker, setCurrencyByTicker] = useState({});
  const [exchangeRateByTicker, setExchangeRateByTicker] = useState({}); // Currency to USD rates
  const [financialSeriesByTicker, setFinancialSeriesByTicker] = useState({});
  const [excludedNoUsdTickers, setExcludedNoUsdTickers] = useState([]);
  const [excludedCadenceTickers, setExcludedCadenceTickers] = useState([]);
  const [treemapColorMetric, setTreemapColorMetric] = useState('profit_margin');
  const [subTab, setSubTab] = useState(0);
  const [trendMetric, setTrendMetric] = useState('revenue');
  const [trendMode, setTrendMode] = useState('normalized');
  const [comparisonCadence, setComparisonCadence] = useState('quarterly');
  const [trendYears, setTrendYears] = useState(5);
  const [trendSupplementalByTicker, setTrendSupplementalByTicker] = useState({});
  const [trendSupplementalLoading, setTrendSupplementalLoading] = useState(false);
  const [priceHistoryByTicker, setPriceHistoryByTicker] = useState({});
  const [priceHistoryLoading, setPriceHistoryLoading] = useState(false);
  const [priceHistoryError, setPriceHistoryError] = useState('');
  const [isRefreshingComparison, setIsRefreshingComparison] = useState(false);
  const hasHydratedComparisonRef = useRef(false);
  const [recentTickers, setRecentTickers] = useState(() => readRecentCompareTickers());

  // Constants for API and caching
  const CACHE_KEY = 'cachedTickers';
  const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  useEffect(() => {
    if (prefilledTickers.length) {
      setCompanies(clampComparisonTickers(prefilledTickers));
      return;
    }

    setCompanies((prev) => (prev.length ? prev : [initialTicker]));
  }, [location.state, initialTicker]);

  // Fetch available tickers from API
  const fetchTickers = async () => {
    let hasUsableCache = false;

    try {
      setLoadingTickers(true);
      setTickersError(null);
      
      // Try to get cached data first
      const cachedData = localStorage.getItem(CACHE_KEY);
      
      if (cachedData) {
        const { data, timestamp } = JSON.parse(cachedData);
        
        // Check if cache is valid AND in the new format (objects with ticker/name)
        const isNewFormat = Array.isArray(data) && data.length > 0 && typeof data[0] === 'object' && data[0].ticker;
        
        if (isNewFormat && Date.now() - timestamp < CACHE_EXPIRY) {
          console.log('Using cached ticker data');
          setTickers(data);
          hasUsableCache = true;
          setLoadingTickers(false);
        } else {
          console.log('Cached ticker data expired or in old format, fetching fresh data');
        }
      }
      
      // Always fetch fresh tickers so newly added symbols are not blocked by cache age.
      const response = await axios.get(`${API_BASE_URL}/api/tickers`);
      setTickers(response.data);
      
      // Cache the fresh data
      localStorage.setItem(
        CACHE_KEY, 
        JSON.stringify({
          data: response.data,
          timestamp: Date.now()
        })
      );
    } catch (error) {
      console.error('Error fetching tickers:', error);
      setTickersError('Failed to load available tickers');
      
      // If API call fails and cache was not already used, use cached/default fallback.
      if (!hasUsableCache) {
        const cachedData = localStorage.getItem(CACHE_KEY);
        if (cachedData) {
          const { data } = JSON.parse(cachedData);
          console.log('API error - falling back to cached data');
          setTickers(data);
        } else {
          // If no cached data is available, provide a small set of default tickers
          setTickers([
            { ticker: 'AAPL', name: 'Apple Inc.' },
            { ticker: 'GOOGL', name: 'Alphabet Inc.' },
            { ticker: 'MSFT', name: 'Microsoft Corporation' },
            { ticker: 'AMZN', name: 'Amazon.com Inc.' }
          ]);
        }
      }
    } finally {
      setLoadingTickers(false);
    }
  };

  // Load tickers from API when component mounts
  useEffect(() => {
    fetchTickers();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadWatchlists = async () => {
      setWatchlistsLoading(true);
      setWatchlistsError('');
      try {
        let ownedRes = null;
        let sharedRes = null;

        for (let attempt = 0; attempt < 3; attempt += 1) {
          const requestHeaders = await buildRequestHeaders();
          [ownedRes, sharedRes] = await Promise.all([
            fetch(`${API_BASE_URL}/api/watchlists`, { headers: requestHeaders }),
            fetch(`${API_BASE_URL}/api/watchlists/shared`, { headers: requestHeaders }),
          ]);

          if (ownedRes.ok && sharedRes.ok) {
            break;
          }

          const shouldRetry = [ownedRes, sharedRes].some((response) => response && [401, 403, 429, 500, 502, 503, 504].includes(response.status));
          if (!shouldRetry || attempt === 2) {
            break;
          }

          await delay(250 * (attempt + 1));
        }

        if (!ownedRes.ok) {
          throw new Error(`Failed to load watchlists (${ownedRes.status})`);
        }
        if (!sharedRes.ok) {
          throw new Error(`Failed to load shared watchlists (${sharedRes.status})`);
        }

        const owned = await ownedRes.json();
        const shared = await sharedRes.json();
        const merged = [
          ...(Array.isArray(owned) ? owned : []).map((list) => ({ ...list, scope: 'owned' })),
          ...(Array.isArray(shared) ? shared : []).map((list) => ({ ...list, scope: 'shared' })),
        ];

        if (!isMounted) {
          return;
        }

        setWatchlists(merged);

        const savedActiveWatchlistId = String(localStorage.getItem(ACTIVE_WATCHLIST_STORAGE_KEY) || '');
        const savedActiveScope = localStorage.getItem(ACTIVE_WATCHLIST_SCOPE_STORAGE_KEY) === 'shared' ? 'shared' : 'owned';
        const nextSelectedWatchlistId = (() => {
          if (selectedWatchlistId && merged.some((list) => String(list.id) === selectedWatchlistId)) {
            return selectedWatchlistId;
          }

          if (savedActiveWatchlistId) {
            const matched = merged.find(
              (list) => String(list.id) === savedActiveWatchlistId && String(list.scope || 'owned') === savedActiveScope
            );
            if (matched) {
              return String(matched.id);
            }
          }

          return merged.length > 0 ? String(merged[0].id) : '';
        })();

        setSelectedWatchlistId(nextSelectedWatchlistId);
      } catch (loadError) {
        console.error('Error loading watchlists for comparison:', loadError);
        if (isMounted) {
          setWatchlistsError(loadError.message || 'Unable to load watchlists');
        }
      } finally {
        if (isMounted) {
          setWatchlistsLoading(false);
        }
      }
    };

    loadWatchlists();

    return () => {
      isMounted = false;
    };
  }, [buildRequestHeaders]);

  // Fetch comparison data when companies list changes
  useEffect(() => {
    if (companies.length === 0) return;
    
    const fetchData = async () => {
      const showBlockingLoader = !hasHydratedComparisonRef.current;
      if (showBlockingLoader) {
        setLoading(true);
      } else {
        setIsRefreshingComparison(true);
      }
      setError(null);
      
      try {
        // First, fetch available periods for each company
        const periodsData = {};
        const currencyData = {};
        const financialSeriesData = {};
        const failedTickers = [];
        const cadenceExcluded = [];
        
        for (const ticker of companies) {
          try {
            // Get financial data
            const financialData = await enhancedFinancialDataService.getFinancialData(ticker, false, comparisonCadence);

            // Get currency for this ticker
            const tickerCurrency = enhancedFinancialDataService.getCurrency(ticker, comparisonCadence);
            currencyData[ticker] = tickerCurrency || 'USD';

            if (!financialData || financialData.length === 0) {
              failedTickers.push(`${ticker}: no financial data`);
              continue;
            }

            // Sort data by date (oldest to newest)
            const sortedData = [...financialData].sort((a, b) =>
              new Date(a.period) - new Date(b.period)
            );

            const cadenceRows = sortedData;
            if (cadenceRows.length === 0) {
              cadenceExcluded.push(ticker);
              failedTickers.push(`${ticker}: no ${comparisonCadence} data`);
              continue;
            }

            financialSeriesData[ticker] = cadenceRows;

            // Store periods for this company
            periodsData[ticker] = cadenceRows.map(item => ({
              period: item.period,
              displayDate: formatPeriodDate(item.period)
            }));

            // Set the latest period as default selected period if not already set
            const latestCadencePeriod = cadenceRows[cadenceRows.length - 1].period;
            const hasSelectedCadencePeriod = periodsData[ticker].some((item) => item.period === selectedPeriods[ticker]);
            if (!selectedPeriods[ticker] || !hasSelectedCadencePeriod) {
              setSelectedPeriods(prev => ({
                ...prev,
                [ticker]: latestCadencePeriod
              }));
            }
          } catch (tickerError) {
            console.warn(`Skipping ${ticker} in comparison load:`, tickerError);
            failedTickers.push(`${ticker}: ${tickerError?.message || 'load failed'}`);
          }
        }

        const loadedTickers = Object.keys(financialSeriesData);
        if (loadedTickers.length === 0) {
          throw new Error(failedTickers.length > 0 ? failedTickers[0] : 'No comparison data available');
        }
        
        // Update available periods and currencies
        setPeriodsByCompany(periodsData);
        setCurrencyByTicker(currencyData);
        setFinancialSeriesByTicker(financialSeriesData);
        setExcludedCadenceTickers(cadenceExcluded);
        
        // Fetch exchange rates BEFORE company metrics so they're available for conversion
        const uniqueCurrencies = [...new Set(Object.values(currencyData))].filter(c => c !== 'USD');
        const exchangeRates = { USD: 1 }; // USD to USD is 1
        await Promise.all(
          uniqueCurrencies.map(async (currency) => {
            try {
              const rateResp = await axios.get(`${API_BASE_URL}/api/exchange-rate/${currency}/USD`);
              if (rateResp.data?.rate) {
                exchangeRates[currency] = rateResp.data.rate;
                console.log(`Exchange rate ${currency} -> USD: ${rateResp.data.rate}`);
              }
            } catch (err) {
              console.warn(`Failed to fetch exchange rate for ${currency}:`, err);
              exchangeRates[currency] = null; // Mark as unavailable
            }
          })
        );
        // Map exchange rates to tickers
        const ratesByTicker = {};
        for (const ticker of companies) {
          const currency = currencyData[ticker] || 'USD';
          if (currency === 'USD') {
            ratesByTicker[ticker] = 1;
          } else {
            const rate = exchangeRates[currency];
            ratesByTicker[ticker] = Number.isFinite(rate) && rate > 0 ? rate : null;
          }
        }
        setExchangeRateByTicker(ratesByTicker);
        
        // Now fetch the metrics for each company with their selected period
        const companyResults = await Promise.allSettled(
          companies.map(async (ticker) => {
            const availablePeriods = periodsData[ticker] || [];
            const selectedCandidate = selectedPeriods[ticker];
            const hasSelectedCandidate = availablePeriods.some((item) => item.period === selectedCandidate);
            const selectedPeriod = hasSelectedCandidate
              ? selectedCandidate
              : (availablePeriods.length > 0 ? availablePeriods[availablePeriods.length - 1].period : null);
            
            if (!selectedPeriod) {
              throw new Error(`No period selected for ${ticker}`);
            }
            
            const metrics = await enhancedFinancialDataService.getCompanyMetrics(ticker, selectedPeriod, comparisonCadence);
            if (!metrics) {
              throw new Error(`No metrics available for ${ticker}`);
            }
            
            // Calculate TTM (trailing twelve months) operating income from last 4 quarters
            const sortedData = financialSeriesData[ticker] || [];
            const last4Quarters = sortedData.slice(-4);
            const ttmOperatingIncome = last4Quarters.reduce((sum, quarter) => {
              const quarterIncome = Number(quarter["Operating Income"]);
              return sum + (Number.isFinite(quarterIncome) ? quarterIncome : 0);
            }, 0);
            const hasValidTTM = last4Quarters.length === 4 && ttmOperatingIncome > 0;

            const freeCashFlow = Number(metrics?.free_cashflow);
            const capex = Number(metrics?.capital_expenditures);
            const capexAsPercentOfFcf = Number.isFinite(freeCashFlow) && Math.abs(freeCashFlow) > 0 && Number.isFinite(capex)
              ? (Math.abs(capex) / Math.abs(freeCashFlow)) * 100
              : null;
            
            // Include the exchange rate directly in the company object
            const currency = currencyData[ticker] || 'USD';
            const exchangeRate = ratesByTicker[ticker];
            const hasUsdComparableData = currency === 'USD' || (Number.isFinite(exchangeRate) && exchangeRate > 0);

            if (!hasUsdComparableData) {
              throw new Error(`missing USD exchange rate for ${currency}`);
            }
            
            return {
              ticker,
              period: selectedPeriod,
              ttmOperatingIncome: hasValidTTM ? ttmOperatingIncome : null,
              capex_as_percent_of_fcf: capexAsPercentOfFcf,
              currency,
              exchangeRate,
              ...metrics,
            };
          })
        );

        const companyData = [];
        const noUsdExcluded = [];
        companyResults.forEach((result, index) => {
          if (result.status === 'fulfilled' && result.value) {
            companyData.push(result.value);
            return;
          }

          const ticker = companies[index];
          console.warn(`Skipping ${ticker} in comparison metrics:`, result.reason);
          const message = String(result.reason?.message || '');
          if (message.includes('missing USD exchange rate')) {
            noUsdExcluded.push(ticker);
          }
          failedTickers.push(`${ticker}: ${result.reason?.message || 'metrics load failed'}`);
        });

        setExcludedNoUsdTickers(noUsdExcluded);

        if (companyData.length === 0) {
          throw new Error(failedTickers.length > 0 ? failedTickers[0] : 'No comparison data available');
        }
        
        setComparisonData(companyData);
        hasHydratedComparisonRef.current = true;

        // Fetch company overview (for outstanding_shares) and stock price for each company
        const overviewResults = {};
        const priceResults = {};
        await Promise.all(
          loadedTickers.map(async (ticker) => {
            try {
              const overview = await enhancedFinancialDataService.getCompanyOverview(ticker);
              if (overview) overviewResults[ticker] = overview;
            } catch (_) { /* non-fatal */ }
            try {
              const priceResp = await axios.get(`${API_BASE_URL}/api/stock-price/${ticker}`);
              if (priceResp.data?.price) priceResults[ticker] = priceResp.data.price;
            } catch (_) { /* non-fatal */ }
          })
        );
        setOverviewByTicker(overviewResults);
        setPriceByTicker(priceResults);

        if (failedTickers.length > 0) {
          console.warn('Partial comparison data load failures:', failedTickers);
        }
      } catch (error) {
        console.error('Error fetching comparison data:', error);
        setError('Failed to fetch comparison data. Please try again.');
        setExcludedNoUsdTickers([]);
        setExcludedCadenceTickers([]);
      } finally {
        setLoading(false);
        setIsRefreshingComparison(false);
      }
    };
    
    fetchData();
  }, [companies, selectedPeriods, comparisonCadence]);

  // Format date for period display
  const formatPeriodDate = (dateString) => {
    const date = new Date(dateString);
    const quarter = Math.floor(date.getMonth() / 3) + 1;
    const year = date.getFullYear();
    return `Q${quarter} ${year}`;
  };

  // Sync all companies to use the same period (nearest to the reference period)
  const syncPeriods = (referencePeriod) => {
    // For each company, find the closest period to the reference period
    const newSelectedPeriods = {};
    
    companies.forEach(ticker => {
      const availablePeriods = periodsByCompany[ticker] || [];
      if (availablePeriods.length === 0) return;
      
      const refDate = new Date(referencePeriod);
      
      // Find the closest period by date difference
      let closestPeriod = availablePeriods[0].period;
      let smallestDiff = Math.abs(new Date(closestPeriod) - refDate);
      
      availablePeriods.forEach(periodData => {
        const diff = Math.abs(new Date(periodData.period) - refDate);
        if (diff < smallestDiff) {
          smallestDiff = diff;
          closestPeriod = periodData.period;
        }
      });
      
      newSelectedPeriods[ticker] = closestPeriod;
    });
    
    setSelectedPeriods(newSelectedPeriods);
  };

  // Generate bar colors based on time periods to visually distinguish different periods
  const getBarColors = () => {
    const uniquePeriods = [...new Set(comparisonData.map(item => item.period))];
    
    // If all companies use the same period, use a single color
    if (uniquePeriods.length === 1) {
      return { revenue: "#8884d8", profit_margin: "#82ca9d" };
    }
    
    // Map each company to a color based on its period
    const colorMap = {};
    const baseColors = [
      "#8884d8", // purple
      "#82ca9d", // green
      "#ff7300", // orange
      "#0088fe", // blue
      "#ff5252", // red
      "#8dd1e1", // teal
    ];
    
    comparisonData.forEach((company, index) => {
      const periodIndex = uniquePeriods.indexOf(company.period);
      const colorIndex = periodIndex % baseColors.length;
      colorMap[company.ticker] = baseColors[colorIndex];
    });
    
    return colorMap;
  };

  const normalizedNewCompany = useMemo(
    () => String(newCompany || '').trim().toUpperCase(),
    [newCompany]
  );

  const canAddTicker =
    normalizedNewCompany
    && !companies.includes(normalizedNewCompany)
    && !loadingTickers
    && companies.length < MAX_COMPARE_TICKERS;

  useEffect(() => {
    if (companies.length === 0) return;

    setRecentTickers((prev) => {
      const merged = clampComparisonTickers([...companies, ...prev]);
      writeRecentCompareTickers(merged);
      return merged;
    });
  }, [companies]);

  // Add company to comparison
  const handleAddCompany = () => {
    const normalizedTicker = String(newCompany || '').trim().toUpperCase();
    if (!normalizedTicker || companies.includes(normalizedTicker) || companies.length >= MAX_COMPARE_TICKERS) return;

    setCompanies([...companies, normalizedTicker]);
    setNewCompany('');
    setTickerPickerInput('');
    setLastRemovedTicker('');
  };

  const handleQuickAddTicker = (tickerToAdd) => {
    const normalizedTicker = String(tickerToAdd || '').trim().toUpperCase();
    if (!normalizedTicker || companies.includes(normalizedTicker) || companies.length >= MAX_COMPARE_TICKERS) {
      return;
    }

    setCompanies((prev) => [...prev, normalizedTicker]);
    setNewCompany('');
    setTickerPickerInput('');
    setLastRemovedTicker('');
  };

  const selectedWatchlist = useMemo(
    () => watchlists.find((list) => String(list.id) === selectedWatchlistId) || null,
    [watchlists, selectedWatchlistId]
  );

  useEffect(() => {
    writeWatchlistStateCache(watchlistStateCacheKey, {
      watchlists,
      selectedWatchlistId,
      savedAt: Date.now(),
    });
  }, [selectedWatchlistId, watchlistStateCacheKey, watchlists]);

  useEffect(() => {
    if (autoPrefillAppliedRef.current) {
      return;
    }

    if (prefilledTickers.length > 0) {
      autoPrefillAppliedRef.current = true;
      return;
    }

    if (!selectedWatchlist || !Array.isArray(selectedWatchlist.tickers) || selectedWatchlist.tickers.length === 0) {
      return;
    }

    const watchlistTickers = normalizeTickers(selectedWatchlist.tickers);
    if (!watchlistTickers.length) {
      autoPrefillAppliedRef.current = true;
      return;
    }

    setCompanies((prev) => {
      const prevNormalized = normalizeTickers(prev);

      if (
        prevNormalized.length === watchlistTickers.length
        && prevNormalized.every((ticker) => watchlistTickers.includes(ticker))
      ) {
        return prev;
      }

      if (prevNormalized.length <= 1) {
        return clampComparisonTickers(watchlistTickers);
      }

      return clampComparisonTickers([...prevNormalized, ...watchlistTickers]);
    });

    autoPrefillAppliedRef.current = true;
  }, [selectedWatchlist, prefilledTickers]);

  const handleAddWatchlistToComparison = () => {
    if (!selectedWatchlist) return;

    const watchlistTickers = normalizeTickers(selectedWatchlist.tickers);
    if (!watchlistTickers.length) return;

    setCompanies((prev) => {
      const merged = [...prev, ...watchlistTickers];
      return clampComparisonTickers(merged);
    });
    setLastRemovedTicker('');
  };

  const resetComparisonState = () => {
    setComparisonData([]);
    setSelectedPeriods({});
    setPeriodsByCompany({});
    setOverviewByTicker({});
    setPriceByTicker({});
    setCurrencyByTicker({});
    setExchangeRateByTicker({});
    setFinancialSeriesByTicker({});
    setExcludedNoUsdTickers([]);
    setExcludedCadenceTickers([]);
    setError(null);
  };

  const handleResetComparisonToWatchlist = () => {
    if (!selectedWatchlist) return;

    const watchlistTickers = normalizeTickers(selectedWatchlist.tickers);
    if (!watchlistTickers.length) return;

    if (companies.length > 0) {
      const shouldReplace = window.confirm('Replace all currently selected tickers with the chosen watchlist?');
      if (!shouldReplace) return;
    }

    setIsRefreshingComparison(true);
    setCompanies(clampComparisonTickers(watchlistTickers));
    setNewCompany('');
    setLastRemovedTicker('');
    resetComparisonState();
  };

  const handleClearAllTickers = () => {
    if (companies.length === 0) return;

    const shouldClear = window.confirm('Clear all tickers from this comparison chart?');
    if (!shouldClear) return;

    setCompanies([]);
    setNewCompany('');
    setLastRemovedTicker('');
    resetComparisonState();
  };

  // Remove company from comparison
  const handleRemoveCompany = (ticker) => {
    setCompanies((prev) => prev.filter((company) => company !== ticker));
    setLastRemovedTicker(ticker);
  };

  const handleUndoRemoveCompany = () => {
    if (!lastRemovedTicker || companies.includes(lastRemovedTicker) || companies.length >= MAX_COMPARE_TICKERS) {
      setLastRemovedTicker('');
      return;
    }

    setCompanies((prev) => [...prev, lastRemovedTicker]);
    setLastRemovedTicker('');
  };

  // Format data for charts - values converted to USD for comparison
  const prepareChartData = (metric) => {
    return comparisonData.map(company => ({
      name: company.ticker,
      value: convertToUSD(company[metric] || 0, company.ticker)
    }));
  };

  // Market cap chart data derived from outstanding_shares × current price (already in USD)
  const prepareMarketCapChartData = () => comparisonData.map((company) => ({
    name: company.ticker,
    value: getMarketCap(company.ticker) || 0,
  }));

  // P/E ratio chart data: Market Cap / TTM Operating Income (converted to USD)
  const preparePERatioChartData = () => {
    return comparisonData.map(company => {
      const mc = getMarketCap(company.ticker);
      const ttmOI = convertToUSD(company.ttmOperatingIncome, company.ticker);
      let peRatio = null;
      if (mc && ttmOI && ttmOI > 0) {
        peRatio = mc / ttmOI;
      }
      return {
        name: company.ticker,
        value: peRatio !== null ? peRatio : 0,
        hasOperatingIncome: ttmOI && ttmOI > 0
      };
    });
  };

  const prepareCapexPctOfFcfChartData = () => {
    return comparisonData.map(company => ({
      name: company.ticker,
      value: Number.isFinite(company.capex_as_percent_of_fcf) ? company.capex_as_percent_of_fcf : 0,
      hasValue: Number.isFinite(company.capex_as_percent_of_fcf),
    }));
  };

  // Calculate optimal Y-axis domain based on data
  const calculateYAxisDomain = (data, buffer = 0.1) => {
    if (!data || data.length === 0) return [0, 0];
    
    const values = data.map(item => item.value);
    const maxValue = Math.max(...values);
    const minValue = Math.min(...values);
    
    // Handle cases with negative values
    if (minValue < 0) {
      // If we have both positive and negative values
      if (maxValue > 0) {
        // Calculate buffer amounts based on the range
        const range = maxValue - minValue;
        const topBuffer = maxValue + (range * buffer);
        const bottomBuffer = minValue - (range * buffer);
        
        return [bottomBuffer, topBuffer];
      } else {
        // All values are negative
        // Make the lower bound more negative by buffer percentage
        return [minValue * (1 + buffer), 0];
      }
    } else {
      // All values are positive or zero
      // For small values, ensure the domain doesn't start exactly at 0
      const topBuffer = maxValue * (1 + buffer);
      const minBuffer = maxValue > 0 ? -(maxValue * 0.05) : -1; // Small negative space for visual clarity
      
      return [minBuffer, topBuffer];
    }
  };

  // Convert a value from a ticker's currency to USD for comparison
  // Uses exchange rate embedded in comparisonData for reliable access
  const convertToUSD = (value, ticker) => {
    const numValue = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(numValue)) return null;

    // First try to get rate from comparisonData (embedded in each company)
    const company = comparisonData.find(c => c.ticker === ticker);
    const currency = company?.currency || currencyByTicker[ticker] || 'USD';
    if (currency === 'USD') return numValue;

    const rate = company?.exchangeRate ?? exchangeRateByTicker[ticker];
    if (!Number.isFinite(rate) || rate <= 0) return null;
    return numValue * rate;
  };

  const comparisonTickers = useMemo(
    () => comparisonData.map((company) => company.ticker),
    [comparisonData]
  );

  // Format values
  const formatCurrency = (value, currency = 'USD') => {
    const symbol = getCurrencySymbol(currency);
    // Convert to number and handle invalid values
    const numValue = typeof value === 'number' ? value : parseFloat(value);
    
    if (isNaN(numValue) || (!numValue && numValue !== 0)) return `${symbol}0`;
    
    const absValue = Math.abs(numValue);
    if (absValue >= 1e12) {
      return `${symbol}${(numValue / 1e12).toFixed(2)}T`;
    } else if (absValue >= 1e9) {
      return `${symbol}${(numValue / 1e9).toFixed(2)}B`;
    } else if (absValue >= 1e6) {
      return `${symbol}${(numValue / 1e6).toFixed(2)}M`;
    } else if (absValue >= 1e3) {
      return `${symbol}${(numValue / 1e3).toFixed(2)}K`;
    }
    return `${symbol}${numValue.toFixed(2)}`;
  };

  const formatCompactCurrencySig4 = (value, currency = 'USD') => {
    const symbol = getCurrencySymbol(currency);
    const numValue = typeof value === 'number' ? value : parseFloat(value);

    if (!Number.isFinite(numValue)) return `${symbol}0`;

    const absValue = Math.abs(numValue);
    if (absValue < 1e6) {
      return formatCurrency(numValue, currency);
    }

    const units = [
      { threshold: 1e12, suffix: 'T' },
      { threshold: 1e9, suffix: 'B' },
      { threshold: 1e6, suffix: 'M' },
    ];

    for (const unit of units) {
      if (absValue >= unit.threshold) {
        const scaled = numValue / unit.threshold;
        const absScaled = Math.abs(scaled);
        const integerDigits = absScaled >= 1 ? Math.floor(Math.log10(absScaled)) + 1 : 1;
        const decimals = Math.max(0, 4 - integerDigits);
        return `${symbol}${scaled.toFixed(decimals)}${unit.suffix}`;
      }
    }

    return formatCurrency(numValue, currency);
  };

  // ADR ratios: ADR price represents this many ordinary shares
  // For market cap: divide shares by ratio when using ADR price
  // Used to correct market cap when SEC reports ordinary shares but price is per ADR
  const ADR_RATIOS = {
    TM: 10,     // Toyota: 1 ADR = 10 shares
    HMC: 1,     // Honda: 1 ADR = 1 share
    BABA: 8,    // Alibaba: 1 ADR = 8 shares
    TSM: 5,     // TSMC: 1 ADR = 5 shares
    JD: 2,      // JD.com: 1 ADR = 2 shares
    NIO: 1,     // NIO: 1 ADR = 1 share
    XPEV: 2,    // XPeng: 1 ADR = 2 shares
    BIDU: 8,    // Baidu: 1 ADR = 8 shares
    PDD: 4,     // Pinduoduo: 1 ADR = 4 shares
    LI: 2,      // Li Auto: 1 ADR = 2 shares
    BEKE: 3,    // KE Holdings: 1 ADR = 3 shares
    NTES: 25,   // NetEase: 1 ADR = 25 shares
    ZTO: 1,     // ZTO Express: 1 ADR = 1 share
    SONY: 1,    // Sony: 1 ADR = 1 share
    ASML: 1,    // ASML: 1:1 (not ADR, direct listing)
    RACE: 1,    // Ferrari: 1:1
    STLA: 1,    // Stellantis: 1:1
  };

  // Compute live market cap for a ticker: outstanding_shares × current price
  // For ADRs: price is per ADR, shares are total company shares, so divide by ADR ratio
  const getMarketCap = (ticker) => {
    const shares = Number(overviewByTicker[ticker]?.outstanding_shares);
    const price = Number(priceByTicker[ticker]);
    if (!Number.isFinite(shares) || shares <= 0 || !Number.isFinite(price) || price <= 0) return null;
    
    // For ADRs, the price is per ADR but shares are total company shares
    // Divide shares by ADR ratio to get equivalent ADR count
    const adrRatio = ADR_RATIOS[ticker] || 1;
    const effectiveShares = shares / adrRatio;
    
    return effectiveShares * price;
  };


  // Diverging color for treemap cells: 0=darkest red, 0.5=neutral dark, 1=brightest green
  const getTreemapCellColor = (normalized) => {
    if (normalized <= 0.5) {
      const intensity = 1 - normalized * 2;
      const lightness = 12 + intensity * 16;
      const saturation = 55 + intensity * 25;
      return `hsl(0, ${saturation}%, ${lightness}%)`;
    } else {
      const intensity = (normalized - 0.5) * 2;
      const lightness = 12 + intensity * 20;
      const saturation = 45 + intensity * 25;
      return `hsl(120, ${saturation}%, ${lightness}%)`;
    }
  };

  const COLOR_METRIC_OPTIONS = [
    { key: 'market_cap',       label: 'Market Cap (USD)',  getValue: (c) => getMarketCap(c.ticker) || 0,                               formatVal: (v) => v > 0 ? formatCompactCurrencySig4(v, 'USD') : 'N/A' },
    { key: 'profit_margin',    label: 'Profit Margin',     getValue: (c) => c.profit_margin || 0,                                          formatVal: (v) => `${v.toFixed(2)}%` },
    { key: 'gross_margin',     label: 'Gross Margin',      getValue: (c) => c.gross_margin || 0,                                           formatVal: (v) => `${v.toFixed(2)}%` },
    { key: 'pe_ratio',         label: 'P/E Ratio (TTM)',   getValue: (c) => { const mc = getMarketCap(c.ticker); const oi = convertToUSD(c.ttmOperatingIncome, c.ticker); return (mc && oi > 0) ? mc / oi : 0; }, formatVal: (v) => v > 0 ? `${v.toFixed(1)}x` : 'N/A' },
    { key: 'revenue',          label: 'Revenue (USD)',     getValue: (c) => { const n = parseFloat(c.revenue); return isFinite(n) ? convertToUSD(n, c.ticker) : 0; },           formatVal: (v) => formatCompactCurrencySig4(v, 'USD') },
    { key: 'assets',           label: 'Assets (USD)',      getValue: (c) => { const n = parseFloat(c.assets); return isFinite(n) ? convertToUSD(n, c.ticker) : 0; },            formatVal: (v) => formatCompactCurrencySig4(v, 'USD') },
    { key: 'liabilities',      label: 'Liabilities (USD)', getValue: (c) => { const n = parseFloat(c.liabilities); return isFinite(n) ? convertToUSD(n, c.ticker) : 0; },       formatVal: (v) => formatCompactCurrencySig4(v, 'USD') },
    { key: 'cash',             label: 'Cash (USD)',        getValue: (c) => { const n = parseFloat(c.cash); return isFinite(n) ? convertToUSD(n, c.ticker) : 0; },              formatVal: (v) => formatCompactCurrencySig4(v, 'USD') },
    { key: 'operating_income', label: 'Operating Income',  getValue: (c) => { const n = parseFloat(c.operating_income); return isFinite(n) ? convertToUSD(n, c.ticker) : 0; },  formatVal: (v) => formatCompactCurrencySig4(v, 'USD') },
    { key: 'earnings',         label: 'Net Income',        getValue: (c) => { const n = parseFloat(c.earnings); return isFinite(n) ? convertToUSD(n, c.ticker) : 0; },          formatVal: (v) => formatCompactCurrencySig4(v, 'USD') },
    { key: 'free_cashflow',    label: 'Free Cash Flow',    getValue: (c) => { const n = parseFloat(c.free_cashflow); return isFinite(n) ? convertToUSD(n, c.ticker) : 0; },     formatVal: (v) => formatCompactCurrencySig4(v, 'USD') },
    { key: 'capital_expenditures', label: 'CapX (USD)',    getValue: (c) => { const n = parseFloat(c.capital_expenditures); return isFinite(n) ? Math.abs(convertToUSD(n, c.ticker) || 0) : 0; }, formatVal: (v) => formatCompactCurrencySig4(v, 'USD') },
    { key: 'operating_cashflow', label: 'Operating Cash Flow (USD)', getValue: (c) => { const n = parseFloat(c.operating_cashflow); return isFinite(n) ? convertToUSD(n, c.ticker) : 0; }, formatVal: (v) => formatCompactCurrencySig4(v, 'USD') },
    { key: 'cashflow_from_financing', label: 'Financing Cash Flow (USD)', getValue: (c) => { const n = parseFloat(c.cashflow_from_financing); return isFinite(n) ? convertToUSD(n, c.ticker) : 0; }, formatVal: (v) => formatCompactCurrencySig4(v, 'USD') },
    { key: 'cashflow_from_investment', label: 'Investing Cash Flow (USD)', getValue: (c) => { const n = parseFloat(c.cashflow_from_investment); return isFinite(n) ? convertToUSD(n, c.ticker) : 0; }, formatVal: (v) => formatCompactCurrencySig4(v, 'USD') },
    { key: 'change_in_cash_and_cash_equivalents', label: 'Change In Cash (USD)', getValue: (c) => { const n = parseFloat(c.change_in_cash_and_cash_equivalents); return isFinite(n) ? convertToUSD(n, c.ticker) : 0; }, formatVal: (v) => formatCompactCurrencySig4(v, 'USD') },
    { key: 'capex_as_percent_of_fcf', label: 'CapEx as % of FCF', getValue: (c) => Number.isFinite(c.capex_as_percent_of_fcf) ? c.capex_as_percent_of_fcf : 0, formatVal: (v) => `${v.toFixed(1)}%` },
  ];

  // Build flat treemap data: size = market cap (fallback: revenue in USD), color = selected metric (converted to USD).
  const buildTreemapData = () => {
    const config = COLOR_METRIC_OPTIONS.find((m) => m.key === treemapColorMetric) || COLOR_METRIC_OPTIONS[0];
    const colorValues = comparisonData.map((c) => config.getValue(c));
    const min = Math.min(...colorValues);
    const max = Math.max(...colorValues);
    const range = max - min;
    return comparisonData.map((company, i) => {
      const mc = getMarketCap(company.ticker);
      const revRaw = parseFloat(company.revenue);
      const revenueUSD = isFinite(revRaw) && revRaw > 0 ? convertToUSD(revRaw, company.ticker) : 0;
      const hasMarketCap = mc !== null && mc > 0;
      const hasRevenue = revenueUSD > 0;
      const sizeValue = hasMarketCap ? mc : (hasRevenue ? revenueUSD : 1);
      const sizeSource = hasMarketCap ? 'Market Cap' : (hasRevenue ? 'Revenue (fallback)' : 'Estimated Size');
      const colorVal = colorValues[i];
      const normalized = range === 0 ? 0.5 : (colorVal - min) / range;
      return {
        name: company.ticker,
        size: sizeValue,
        sizeSource,
        displaySize: formatCompactCurrencySig4(sizeValue, 'USD'),
        colorVal,
        metricDisplayValue: config.formatVal(colorVal),
        bgColor: getTreemapCellColor(normalized),
      };
    });
  };

  const sharedChartTooltipProps = {
    contentStyle: {
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(17, 24, 39, 0.96)' : '#ffffff',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
    },
    labelStyle: {
      color: theme.palette.mode === 'dark' ? '#f3f4f6' : '#111827',
      fontWeight: 600,
    },
  };

  const TREND_METRIC_OPTIONS = [
    { key: 'price', label: 'Share Price (USD)', isPercent: false },
    { key: 'revenue', label: 'Revenue (USD)', isPercent: false },
    { key: 'assets', label: 'Assets (USD)', isPercent: false },
    { key: 'liabilities', label: 'Liabilities (USD)', isPercent: false },
    { key: 'operating_income', label: 'Operating Income (USD)', isPercent: false },
    { key: 'gross_margin', label: 'Gross Margin (%)', isPercent: true },
    { key: 'earnings', label: 'Net Income (USD)', isPercent: false },
    { key: 'free_cashflow', label: 'Free Cash Flow (USD)', isPercent: false },
    { key: 'operating_cashflow', label: 'Operating Cash Flow (USD)', isPercent: false },
    { key: 'cashflow_from_financing', label: 'Financing Cash Flow (USD)', isPercent: false },
    { key: 'cashflow_from_investment', label: 'Investing Cash Flow (USD)', isPercent: false },
    { key: 'change_in_cash_and_cash_equivalents', label: 'Change In Cash (USD)', isPercent: false },
    { key: 'capital_expenditures', label: 'CapX (USD)', isPercent: false },
    { key: 'capex_as_percent_of_fcf', label: 'CapEx as % of FCF', isPercent: true },
    { key: 'cash', label: 'Cash (USD)', isPercent: false },
    { key: 'profit_margin', label: 'Profit Margin (%)', isPercent: true },
  ];

  const TREND_METRIC_DETAILS = {
    price: {
      brief: 'Adjusted historical closing share price, including split and dividend adjustments where available.',
      formula: 'Adjusted Close (USD) from Yahoo Finance market history',
    },
    market_cap: {
      brief: 'Total equity value derived from live quote and shares outstanding.',
      formula: 'Market Cap = Share Price x Outstanding Shares (ADR-adjusted where applicable)',
    },
    revenue: {
      brief: 'Top-line sales for selected periods, normalized to USD for cross-company comparison.',
      formula: 'Revenue (USD) = Reported Revenue x FX Rate to USD',
    },
    assets: {
      brief: 'Total assets from balance-sheet snapshots, converted to USD.',
      formula: 'Assets (USD) = Reported Assets x FX Rate to USD',
    },
    liabilities: {
      brief: 'Total liabilities from balance-sheet snapshots, converted to USD.',
      formula: 'Liabilities (USD) = Reported Liabilities x FX Rate to USD',
    },
    operating_income: {
      brief: 'Core operating profit trend, normalized to USD.',
      formula: 'Operating Income (USD) = Reported Operating Income x FX Rate to USD',
    },
    gross_margin: {
      brief: 'Gross profitability as a percent of revenue.',
      formula: 'Gross Margin (%) = Gross Profit / Revenue x 100',
    },
    earnings: {
      brief: 'Net income trend where available, with fallback logic from statement data.',
      formula: 'Net Income (USD) = Earnings x FX Rate to USD',
    },
    free_cashflow: {
      brief: 'Cash generated after capital expenditures, converted to USD.',
      formula: 'FCF (USD) = (Operating Cash Flow - CapEx) x FX Rate to USD',
    },
    operating_cashflow: {
      brief: 'Cash generated by core operations, converted to USD.',
      formula: 'Operating Cash Flow (USD) = Reported OCF x FX Rate to USD',
    },
    cashflow_from_financing: {
      brief: 'Net financing cash flow trend, converted to USD.',
      formula: 'Financing CF (USD) = Reported Financing CF x FX Rate to USD',
    },
    cashflow_from_investment: {
      brief: 'Net investing cash flow trend, converted to USD.',
      formula: 'Investing CF (USD) = Reported Investing CF x FX Rate to USD',
    },
    change_in_cash_and_cash_equivalents: {
      brief: 'Net period-over-period cash change, converted to USD.',
      formula: 'Change in Cash (USD) = Reported Change in Cash x FX Rate to USD',
    },
    pe_ratio: {
      brief: 'Proxy valuation multiple for comparisons when trailing earnings are uneven.',
      formula: 'P/E Proxy = Market Cap / TTM Operating Income',
    },
    capital_expenditures: {
      brief: 'Capital spending trend, shown as absolute value and converted to USD.',
      formula: 'CapEx (USD) = abs(Reported CapEx) x FX Rate to USD',
    },
    capex_as_percent_of_fcf: {
      brief: 'How much free cash flow is consumed by capital expenditures.',
      formula: 'CapEx as % of FCF = abs(CapEx) / abs(Free Cash Flow) x 100',
    },
    cash: {
      brief: 'Cash and equivalents from statement snapshots, converted to USD.',
      formula: 'Cash (USD) = Reported Cash x FX Rate to USD',
    },
    profit_margin: {
      brief: 'Bottom-line profitability as a percent of revenue.',
      formula: 'Profit Margin (%) = Net Income / Revenue x 100',
    },
  };

  const toFiniteOrNull = (value) => {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = typeof value === 'number' ? value : parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const getFirstFiniteFromRow = (row, keys) => {
    for (const key of keys) {
      const value = toFiniteOrNull(row?.[key]);
      if (value !== null) return value;
    }
    return null;
  };

  const needsSupplementalTrendMetrics = [
    'assets',
    'liabilities',
    'capital_expenditures',
    'cash',
    'free_cashflow',
    'operating_cashflow',
    'cashflow_from_financing',
    'cashflow_from_investment',
    'change_in_cash_and_cash_equivalents',
    'capex_as_percent_of_fcf',
  ].includes(trendMetric);

  useEffect(() => {
    let cancelled = false;

    if (!needsSupplementalTrendMetrics || companies.length === 0) {
      setTrendSupplementalLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const loadSupplementalTrendMetrics = async () => {
      setTrendSupplementalLoading(true);
      try {
        const tickerEntries = await Promise.all(
          companies.map(async (ticker) => {
            const series = financialSeriesByTicker[ticker] || [];
            const periodMap = {};

            await Promise.all(
              series.map(async (row) => {
                const periodKey = String(row?.period || '').slice(0, 10);
                if (!periodKey) return;

                try {
                  const [metrics, cashFlowData] = await Promise.all([
                    enhancedFinancialDataService.getCompanyMetrics(ticker, row.period, comparisonCadence),
                    trendMetric === 'change_in_cash_and_cash_equivalents'
                      ? enhancedFinancialDataService.getCashFlow(ticker, row.period)
                      : Promise.resolve(null),
                  ]);
                  const cashFlowRow = Array.isArray(cashFlowData) ? cashFlowData[0] : cashFlowData;
                  const capexValue = toFiniteOrNull(metrics?.capital_expenditures);
                  const cashValue = toFiniteOrNull(metrics?.cash);
                  const freeCashFlowValue = toFiniteOrNull(metrics?.free_cashflow);
                  const assetsValue = toFiniteOrNull(metrics?.assets);
                  const liabilitiesValue = toFiniteOrNull(metrics?.liabilities);
                  const operatingCashflowValue = toFiniteOrNull(metrics?.operating_cashflow);
                  const financingCashflowValue = toFiniteOrNull(metrics?.cashflow_from_financing);
                  const investingCashflowValue = toFiniteOrNull(metrics?.cashflow_from_investment);
                  const reportedChangeInCash =
                    toFiniteOrNull(cashFlowRow?.change_in_cash_and_cash_equivalents)
                    ?? toFiniteOrNull(cashFlowRow?.changeInCashAndCashEquivalents)
                    ?? toFiniteOrNull(cashFlowRow?.net_change_in_cash)
                    ?? toFiniteOrNull(cashFlowRow?.netChangeInCash);
                  const cashFlowComponents = [
                    toFiniteOrNull(cashFlowRow?.operating_cashflow) ?? toFiniteOrNull(cashFlowRow?.operatingCashflow),
                    toFiniteOrNull(cashFlowRow?.cashflow_from_investment) ?? toFiniteOrNull(cashFlowRow?.cashflowFromInvestment),
                    toFiniteOrNull(cashFlowRow?.cashflow_from_financing) ?? toFiniteOrNull(cashFlowRow?.cashflowFromFinancing),
                  ];
                  const derivedChangeInCash = cashFlowComponents.every((value) => value !== null)
                    ? cashFlowComponents.reduce((sum, value) => sum + value, 0)
                    : null;
                  const changeInCashValue = reportedChangeInCash
                    ?? derivedChangeInCash
                    ?? toFiniteOrNull(metrics?.change_in_cash_and_cash_equivalents);
                  const capexPctOfFcf = Number.isFinite(freeCashFlowValue) && Math.abs(freeCashFlowValue) > 0 && Number.isFinite(capexValue)
                    ? (Math.abs(capexValue) / Math.abs(freeCashFlowValue)) * 100
                    : null;

                  periodMap[periodKey] = {
                    capital_expenditures: capexValue !== null ? Math.abs(capexValue) : null,
                    cash: cashValue,
                    free_cashflow: freeCashFlowValue,
                    assets: assetsValue,
                    liabilities: liabilitiesValue,
                    operating_cashflow: operatingCashflowValue,
                    cashflow_from_financing: financingCashflowValue,
                    cashflow_from_investment: investingCashflowValue,
                    change_in_cash_and_cash_equivalents: changeInCashValue,
                    capex_as_percent_of_fcf: capexPctOfFcf,
                  };
                } catch (metricsError) {
                  periodMap[periodKey] = {
                    capital_expenditures: null,
                    cash: null,
                    free_cashflow: null,
                    assets: null,
                    liabilities: null,
                    operating_cashflow: null,
                    cashflow_from_financing: null,
                    cashflow_from_investment: null,
                    change_in_cash_and_cash_equivalents: null,
                    capex_as_percent_of_fcf: null,
                  };
                }
              })
            );

            return [ticker, periodMap];
          })
        );

        if (!cancelled) {
          setTrendSupplementalByTicker(Object.fromEntries(tickerEntries));
        }
      } catch (error) {
        console.error('Error loading trend supplemental metrics:', error);
        if (!cancelled) {
          setTrendSupplementalByTicker({});
        }
      } finally {
        if (!cancelled) {
          setTrendSupplementalLoading(false);
        }
      }
    };

    loadSupplementalTrendMetrics();

    return () => {
      cancelled = true;
    };
  }, [companies, financialSeriesByTicker, needsSupplementalTrendMetrics, trendMetric, comparisonCadence]);

  const trendMetricConfig = TREND_METRIC_OPTIONS.find((option) => option.key === trendMetric) || TREND_METRIC_OPTIONS[0];

  useEffect(() => {
    if (trendMetric !== 'price' || comparisonTickers.length === 0) {
      setPriceHistoryLoading(false);
      setPriceHistoryError('');
      return undefined;
    }

    const controller = new AbortController();
    const loadPriceHistory = async () => {
      setPriceHistoryLoading(true);
      setPriceHistoryError('');

      const results = await Promise.allSettled(comparisonTickers.map(async (ticker) => {
        const response = await fetch(
          `${API_BASE_URL}/api/stock-price-history/${encodeURIComponent(ticker)}?years=${trendYears}`,
          { signal: controller.signal }
        );
        if (!response.ok) throw new Error(`${ticker} price history failed (${response.status})`);
        const payload = await response.json();
        return [ticker, payload];
      }));

      if (controller.signal.aborted) return;
      const loadedEntries = results
        .filter((result) => result.status === 'fulfilled' && Array.isArray(result.value[1]?.points))
        .map((result) => result.value);
      setPriceHistoryByTicker(Object.fromEntries(loadedEntries));

      const failedCount = results.length - loadedEntries.length;
      if (failedCount) {
        setPriceHistoryError(`${failedCount} compan${failedCount === 1 ? 'y' : 'ies'} could not load price history.`);
      }
      setPriceHistoryLoading(false);
    };

    loadPriceHistory().catch((loadError) => {
      if (loadError.name === 'AbortError') return;
      setPriceHistoryByTicker({});
      setPriceHistoryError(loadError.message || 'Unable to load historical prices.');
      setPriceHistoryLoading(false);
    });

    return () => controller.abort();
  }, [comparisonTickers, trendMetric, trendYears]);

  const metricTrendChartData = useMemo(() => {
    const cutoffDate = new Date();
    if (trendYears < 30) {
      cutoffDate.setFullYear(cutoffDate.getFullYear() - trendYears);
    }

    const byDate = new Map();

    if (trendMetric === 'price') {
      comparisonTickers.forEach((ticker) => {
        const points = priceHistoryByTicker[ticker]?.points || [];
        points.forEach((point) => {
          const date = new Date(point?.date);
          const value = convertToUSD(point?.value, ticker);
          if (Number.isNaN(date.getTime()) || !Number.isFinite(value)) return;
          if (trendYears < 30 && date < cutoffDate) return;

          const dateKey = date.toISOString().slice(0, 10);
          if (!byDate.has(dateKey)) byDate.set(dateKey, { date: dateKey });
          byDate.get(dateKey)[ticker] = value;
        });
      });
      return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
    }

    companies.forEach((ticker) => {
      const series = financialSeriesByTicker[ticker] || [];
      let lastKnownCashValue = null;
      series.forEach((row) => {
        const date = new Date(row?.period);
        if (Number.isNaN(date.getTime())) return;
        if (trendYears < 30 && date < cutoffDate) return;

        const dateKey = date.toISOString().slice(0, 10);
        const revenue = getFirstFiniteFromRow(row, ['Revenue', 'revenue']);
        const operatingIncome = getFirstFiniteFromRow(row, ['Operating Income', 'operating_income']);
        const netIncome = getFirstFiniteFromRow(row, ['Net Income', 'net_income']);
        const resolvedEarnings = netIncome !== null
          ? netIncome
          : (operatingIncome !== null ? operatingIncome * 0.8 : null);
        const grossMargin = revenue !== null && revenue !== 0 && operatingIncome !== null
          ? ((operatingIncome * 1.5) / revenue) * 100
          : null;
        const supplemental = trendSupplementalByTicker?.[ticker]?.[dateKey] || null;
        const marketCap = getMarketCap(ticker);
        let metricValue = null;

        if (trendMetric === 'market_cap') {
          metricValue = marketCap !== null ? marketCap : null;
        } else if (trendMetric === 'revenue') {
          metricValue = revenue !== null ? convertToUSD(revenue, ticker) : null;
        } else if (trendMetric === 'assets') {
          const assetsValue = toFiniteOrNull(supplemental?.assets);
          metricValue = assetsValue !== null ? convertToUSD(assetsValue, ticker) : null;
        } else if (trendMetric === 'liabilities') {
          const liabilitiesValue = toFiniteOrNull(supplemental?.liabilities);
          metricValue = liabilitiesValue !== null ? convertToUSD(liabilitiesValue, ticker) : null;
        } else if (trendMetric === 'operating_income') {
          metricValue = operatingIncome !== null ? convertToUSD(operatingIncome, ticker) : null;
        } else if (trendMetric === 'gross_margin') {
          metricValue = grossMargin;
        } else if (trendMetric === 'earnings') {
          metricValue = resolvedEarnings !== null ? convertToUSD(resolvedEarnings, ticker) : null;
        } else if (trendMetric === 'free_cashflow') {
          const freeCashFlowValue = toFiniteOrNull(supplemental?.free_cashflow);
          metricValue = freeCashFlowValue !== null ? convertToUSD(freeCashFlowValue, ticker) : null;
        } else if (trendMetric === 'operating_cashflow') {
          const operatingCashflowValue = toFiniteOrNull(supplemental?.operating_cashflow);
          metricValue = operatingCashflowValue !== null ? convertToUSD(operatingCashflowValue, ticker) : null;
        } else if (trendMetric === 'cashflow_from_financing') {
          const financingCashflowValue = toFiniteOrNull(supplemental?.cashflow_from_financing);
          metricValue = financingCashflowValue !== null ? convertToUSD(financingCashflowValue, ticker) : null;
        } else if (trendMetric === 'cashflow_from_investment') {
          const investingCashflowValue = toFiniteOrNull(supplemental?.cashflow_from_investment);
          metricValue = investingCashflowValue !== null ? convertToUSD(investingCashflowValue, ticker) : null;
        } else if (trendMetric === 'change_in_cash_and_cash_equivalents') {
          const changeInCashValue = toFiniteOrNull(supplemental?.change_in_cash_and_cash_equivalents);
          metricValue = changeInCashValue !== null ? convertToUSD(changeInCashValue, ticker) : null;
        } else if (trendMetric === 'pe_ratio') {
          metricValue = marketCap !== null && operatingIncome !== null && operatingIncome > 0
            ? marketCap / convertToUSD(operatingIncome, ticker)
            : null;
        } else if (trendMetric === 'capital_expenditures') {
          const capexValue = toFiniteOrNull(supplemental?.capital_expenditures);
          metricValue = capexValue !== null ? convertToUSD(capexValue, ticker) : null;
        } else if (trendMetric === 'capex_as_percent_of_fcf') {
          metricValue = toFiniteOrNull(supplemental?.capex_as_percent_of_fcf);
        } else if (trendMetric === 'cash') {
          const cashValue = toFiniteOrNull(supplemental?.cash);
          if (cashValue !== null) {
            lastKnownCashValue = convertToUSD(cashValue, ticker);
          }
          metricValue = Number.isFinite(lastKnownCashValue) ? lastKnownCashValue : null;
        } else if (trendMetric === 'profit_margin') {
          metricValue = revenue !== null && revenue !== 0 && resolvedEarnings !== null
            ? (resolvedEarnings / revenue) * 100
            : null;
        }

        if (!Number.isFinite(metricValue)) return;

        if (!byDate.has(dateKey)) byDate.set(dateKey, { date: dateKey });
        byDate.get(dateKey)[ticker] = metricValue;
      });
    });

    return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [companies, comparisonTickers, financialSeriesByTicker, trendMetric, trendYears, convertToUSD, trendSupplementalByTicker, getMarketCap, priceHistoryByTicker]);

  const displayedMetricTrendChartData = useMemo(() => {
    if (trendMode === 'absolute') return metricTrendChartData;

    const baseValues = {};
    comparisonTickers.forEach((ticker) => {
      const firstRow = metricTrendChartData.find((row) => Number.isFinite(row?.[ticker]) && row[ticker] !== 0);
      baseValues[ticker] = firstRow?.[ticker] ?? null;
    });

    return metricTrendChartData.map((row) => {
      const indexedRow = { date: row.date };
      comparisonTickers.forEach((ticker) => {
        const value = row[ticker];
        const baseValue = baseValues[ticker];
        indexedRow[ticker] = Number.isFinite(value) && Number.isFinite(baseValue)
          ? (value / baseValue) * 100
          : null;
      });
      return indexedRow;
    });
  }, [comparisonTickers, metricTrendChartData, trendMode]);

  const trendSeriesLastPointIndex = useMemo(() => {
    const indexes = {};
    comparisonTickers.forEach((ticker) => {
      indexes[ticker] = -1;
      for (let i = displayedMetricTrendChartData.length - 1; i >= 0; i -= 1) {
        const value = displayedMetricTrendChartData[i]?.[ticker];
        if (Number.isFinite(value)) {
          indexes[ticker] = i;
          break;
        }
      }
    });
    return indexes;
  }, [comparisonTickers, displayedMetricTrendChartData]);

  const trendMetricDetail = TREND_METRIC_DETAILS[trendMetric] || {
    brief: 'Trend metric selected for cross-company historical comparison.',
    formula: 'See source metric definition.',
  };

  const formatTrendDate = (value, full = false) => {
    if (trendMetric !== 'price') return formatPeriodDate(value);
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('en-US', full
      ? { month: 'short', day: 'numeric', year: 'numeric' }
      : { month: 'short', year: '2-digit' });
  };

  const isTrendMetricLoading = needsSupplementalTrendMetrics
    ? trendSupplementalLoading
    : trendMetric === 'price' && priceHistoryLoading;

  const comparisonControlsPanel = (
    <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2 }, mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.25 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Edit comparison
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {companies.length} of {MAX_COMPARE_TICKERS} tickers selected
          </Typography>
        </Box>
        <Tooltip title="Clear all tickers">
          <span>
            <IconButton size="small" onClick={handleClearAllTickers} disabled={companies.length === 0}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.75,
          minHeight: 32,
          mb: 1.25,
        }}
      >
        {companies.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Search below to add a ticker.
          </Typography>
        )}
        {companies.map((ticker) => (
          <Chip
            key={ticker}
            label={ticker}
            size="small"
            color="primary"
            onDelete={() => handleRemoveCompany(ticker)}
          />
        ))}
      </Box>

      <Box sx={{ pt: 1.25, borderTop: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Autocomplete
            sx={{ flex: '2 1 320px', minWidth: 220 }}
            options={tickers}
            value={null}
            inputValue={tickerPickerInput}
            onInputChange={(_, nextInput, reason) => {
              if (reason === 'reset') return;
              setTickerPickerInput(nextInput);
              setNewCompany(nextInput.toUpperCase());
            }}
            onChange={(event, newValue) => {
              const ticker = typeof newValue === 'string' ? newValue : (newValue?.ticker || '');
              if (!ticker) return;
              setNewCompany(ticker.toUpperCase());
              setTickerPickerInput(ticker.toUpperCase());
              handleQuickAddTicker(ticker);
            }}
            autoHighlight
            selectOnFocus
            clearOnBlur={false}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              if (!option?.ticker) return '';
              return option?.name && option.name !== option.ticker
                ? `${option.ticker} - ${option.name}`
                : option.ticker;
            }}
            filterOptions={tickerFilterOptions}
            renderOption={(props, option) => {
              const { key, ...restProps } = props;
              return (
                <li key={option?.ticker || option} {...restProps}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{option?.ticker || option}</Typography>
                    {option?.name && option.name !== option.ticker && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>{option.name}</Typography>
                    )}
                  </Box>
                </li>
              );
            }}
            isOptionEqualToValue={(option, value) => {
              const optionTicker = typeof option === 'string' ? option : option?.ticker;
              const valueTicker = typeof value === 'string' ? value : value?.ticker;
              return optionTicker === valueTicker;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search ticker or company"
                size="small"
                fullWidth
                disabled={loadingTickers || companies.length >= MAX_COMPARE_TICKERS}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && canAddTicker) {
                    event.preventDefault();
                    handleAddCompany();
                  }
                }}
              />
            )}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleAddCompany}
            disabled={!canAddTicker}
            startIcon={<AddIcon />}
            sx={{ height: 40, px: 2, whiteSpace: 'nowrap' }}
          >
            Add
          </Button>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select
              value={comparisonCadence}
              onChange={(event) => setComparisonCadence(String(event.target.value))}
            >
              <MenuItem value="quarterly">Quarterly</MenuItem>
              <MenuItem value="annual">Annual</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
          <FormControl size="small" sx={{ flex: '1 1 260px', minWidth: 220 }} disabled={watchlistsLoading || watchlists.length === 0}>
            <Select
              value={selectedWatchlistId}
              onChange={(event) => setSelectedWatchlistId(String(event.target.value))}
              displayEmpty
              renderValue={(value) => {
                if (!value) return watchlistsLoading ? 'Loading watchlists...' : 'Select watchlist';
                const selected = watchlists.find((list) => String(list.id) === String(value));
                return selected ? `${selected.scope === 'shared' ? 'Shared' : 'My'}: ${selected.name}` : 'Select watchlist';
              }}
            >
              {watchlists.map((list) => (
                <MenuItem key={`${list.scope}-${list.id}`} value={String(list.id)}>
                  {`${list.scope === 'shared' ? 'Shared' : 'My'}: ${list.name}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            color="secondary"
            onClick={handleAddWatchlistToComparison}
            disabled={!selectedWatchlist || !Array.isArray(selectedWatchlist?.tickers) || selectedWatchlist.tickers.length === 0 || companies.length >= MAX_COMPARE_TICKERS}
            sx={{ height: 40, whiteSpace: 'nowrap' }}
          >
            Merge
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="secondary"
            onClick={handleResetComparisonToWatchlist}
            disabled={isRefreshingComparison || !selectedWatchlist || !Array.isArray(selectedWatchlist?.tickers) || selectedWatchlist.tickers.length === 0}
            startIcon={isRefreshingComparison ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{ height: 40, whiteSpace: 'nowrap' }}
          >
            {isRefreshingComparison ? 'Loading...' : 'Replace'}
          </Button>
        </Box>

        {recentTickers.some((symbol) => !companies.includes(symbol)) && (
          <Box
            sx={{ mt: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Recent</Typography>
            {recentTickers
              .filter((symbol) => !companies.includes(symbol))
              .slice(0, 8)
              .map((symbol) => (
              <Chip
                key={`recent-${symbol}`}
                size="small"
                variant="outlined"
                label={symbol}
                onClick={() => handleQuickAddTicker(symbol)}
              />
            ))}
          </Box>
        )}
      </Box>

      {lastRemovedTicker && (
        <Button
          size="small"
          variant="outlined"
          onClick={handleUndoRemoveCompany}
          startIcon={<UndoIcon />}
          sx={{ height: 28, borderRadius: 999, mt: 1 }}
        >
          Undo remove {lastRemovedTicker}
        </Button>
      )}
      {normalizedNewCompany && companies.includes(normalizedNewCompany) && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.6 }}>
          {normalizedNewCompany} is already in the comparison set.
        </Typography>
      )}
      {watchlistsError && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          {watchlistsError}
        </Typography>
      )}
    </Paper>
  );

  return (
    <Box sx={{ px: { xs: 1, sm: 1.5, lg: 1 }, py: { xs: 1, sm: 2 } }}>
      {(loading || isRefreshingComparison) && comparisonData.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading comparison data...
          </Typography>
        </Box>
      ) : comparisonData.length > 0 ? (
        <>
          {error && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {excludedNoUsdTickers.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Excluded from comparison due to missing USD-convertible financial data: {excludedNoUsdTickers.join(', ')}
            </Alert>
          )}
          {excludedCadenceTickers.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Excluded because they do not have {comparisonCadence} financial periods available: {excludedCadenceTickers.join(', ')}
            </Alert>
          )}
          {comparisonData.length > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
              {new Set(comparisonData.map(c => c.period)).size > 1 ? (
                <>
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={`Different periods selected (${new Set(comparisonData.map(c => c.period)).size})`}
                    onClick={() => setPeriodsExpanded((prev) => !prev)}
                    onDelete={() => setPeriodsExpanded((prev) => !prev)}
                    deleteIcon={periodsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  />
                  <Button
                    variant="text"
                    size="small"
                    color="primary"
                    onClick={() => syncPeriods(comparisonData[0].period)}
                    startIcon={<SyncIcon />}
                  >
                    Synchronize Periods
                  </Button>
                  <Tooltip title='You can select a different time period for each company using the dropdown menus in the "Period" row below.'>
                    <InfoOutlinedIcon fontSize="small" color="action" />
                  </Tooltip>
                </>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  <CheckCircleIcon fontSize="inherit" color="success" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  All companies compared using the same period: <strong>{formatPeriodDate(comparisonData[0]?.period)}</strong>
                </Typography>
              )}
            </Box>
          )}
          {new Set(comparisonData.map(c => c.period)).size > 1 && (
            <Collapse in={periodsExpanded} timeout="auto">
              <Paper elevation={1} sx={{ p: 1.5, mb: 1.5 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Different Periods Selected:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {comparisonData.map(company => (
                    <Chip
                      key={`legend-${company.ticker}`}
                      label={`${company.ticker}: ${formatPeriodDate(company.period)}`}
                      sx={{
                        bgcolor: getBarColors()[company.ticker],
                        color: '#fff',
                        fontWeight: 'bold'
                      }}
                    />
                  ))}
                </Box>
              </Paper>
            </Collapse>
          )}

          {/* Subtabs: Market Treemap / Metric Trends / Bar Charts */}
          <Box sx={{ ...COMPARISON_SECTION_SX, borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={subTab} onChange={(_, v) => setSubTab(v)}>
              <Tab value={0} label="Latest Quarter Treemap" />
              <Tab value={2} label="Latest Quarter Bar Charts" />
              <Tab value={1} label="Metric Trends" />
            </Tabs>
          </Box>

          {subTab === 0 && <Paper elevation={2} sx={{ ...COMPARISON_SECTION_SX, p: { xs: 1.5, sm: 2, lg: 2.5 }, mb: 3, position: 'relative' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6">Market Treemap</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">Color by:</Typography>
                <FormControl size="small" sx={{ minWidth: 165 }}>
                  <Select
                    value={treemapColorMetric}
                    onChange={(e) => setTreemapColorMetric(e.target.value)}
                  >
                    {COLOR_METRIC_OPTIONS.map((opt) => (
                      <MenuItem key={opt.key} value={opt.key}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Rectangle size = Market Cap (or Revenue if unavailable). Color = {COLOR_METRIC_OPTIONS.find((m) => m.key === treemapColorMetric)?.label}.
            </Typography>
            {isRefreshingComparison && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 3,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.45)' : 'rgba(255, 255, 255, 0.72)',
                  backdropFilter: 'blur(1.5px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <CircularProgress size={36} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Refreshing treemap metrics...
                </Typography>
              </Box>
            )}
            <ResponsiveContainer width="100%" height={560}>
              <Treemap
                data={buildTreemapData()}
                dataKey="size"
                aspectRatio={16 / 7}
                isAnimationActive={false}
                content={(props) => {
                  const { x, y, width, height, name, metricDisplayValue, bgColor } = props;
                  if (!name || !width || !height || width < 4 || height < 4) return null;
                  const horizontalPadding = 12;
                  const verticalPadding = 10;
                  const innerWidth = Math.max(width - horizontalPadding, 0);
                  const innerHeight = Math.max(height - verticalPadding, 0);
                  const maxTickerChars = Math.max(Math.floor(innerWidth / 17), 0);
                  const truncatedName =
                    maxTickerChars >= name.length
                      ? name
                      : maxTickerChars > 3
                        ? `${name.slice(0, maxTickerChars - 1)}…`
                        : '';
                  const showLabel = Boolean(truncatedName) && innerWidth >= 48 && innerHeight >= 26;
                  const rawFontSize = Math.min(
                    Math.floor(innerWidth / Math.max(truncatedName.length * 0.72, 1)),
                    Math.floor(innerHeight * 0.26),
                    42
                  );
                  const fontSize = Math.max(rawFontSize, showLabel ? 10 : 0);
                  const valueFontSize = Math.max(Math.floor(fontSize * 0.44), 8);
                  const valueBadgeWidth = Math.max(Math.min(width * 0.8, 180), 56);
                  const valueBadgeHeight = Math.max(Math.min(height * 0.32, 34), 16);
                  const valueBadgeX = x + (width - valueBadgeWidth) / 2;
                  const valueBadgeY = y + height / 2 + fontSize * 0.08;
                  const showValue =
                    showLabel &&
                    innerWidth >= 92 &&
                    innerHeight >= 70 &&
                    metricDisplayValue &&
                    metricDisplayValue.length <= Math.max(Math.floor(innerWidth / 11), 4);
                  return (
                    <g>
                      <rect
                        x={x + 1}
                        y={y + 1}
                        width={Math.max(width - 2, 0)}
                        height={Math.max(height - 2, 0)}
                        style={{ fill: bgColor || '#1a1a2a', stroke: '#0d0d0d', strokeWidth: 2 }}
                      />
                      {showLabel && (
                        <text
                          x={x + width / 2}
                          y={y + height / 2 + (showValue ? -fontSize * 0.42 : 0)}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          style={{
                            fill: 'rgba(245,247,250,0.94)',
                            fontSize,
                            fontWeight: 800,
                            fontFamily: FINANCIAL_FONT_STACK,
                            letterSpacing: 0.25,
                            textShadow: '0 1px 2px rgba(15, 23, 42, 0.85)',
                            pointerEvents: 'none',
                          }}
                        >
                          {truncatedName}
                        </text>
                      )}
                      {showValue && (
                        <>
                          <rect
                            x={valueBadgeX}
                            y={valueBadgeY}
                            width={valueBadgeWidth}
                            height={valueBadgeHeight}
                            rx={6}
                            fill="rgba(15, 23, 42, 0.42)"
                          />
                          <text
                            x={x + width / 2}
                            y={valueBadgeY + valueBadgeHeight / 2}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            style={{
                              fill: 'rgba(248,250,252,0.98)',
                              fontSize: valueFontSize,
                              fontWeight: 700,
                              fontFamily: FINANCIAL_NUMERIC_FONT_STACK,
                              letterSpacing: 0.15,
                              fontVariantNumeric: 'tabular-nums',
                              textShadow: '0 1px 2px rgba(15, 23, 42, 0.9)',
                              pointerEvents: 'none',
                            }}
                          >
                            {metricDisplayValue}
                          </text>
                        </>
                      )}
                    </g>
                  );
                }}
              >
                <RechartsTooltip
                  content={({ payload }) => {
                    if (!payload || !payload.length) return null;
                    const data = payload[0]?.payload;
                    if (!data) return null;
                    return (
                      <Box sx={{
                        bgcolor: 'rgba(20,20,30,0.95)',
                        border: '1px solid rgba(100,100,120,0.5)',
                        borderRadius: 1,
                        p: 1.5,
                        minWidth: 120,
                      }}>
                        <Typography sx={{ fontWeight: 'bold', fontSize: 14, color: '#fff' }}>
                          {data.name}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'rgba(180,200,180,0.9)', mt: 0.5 }}>
                          {data.sizeSource}: {formatCompactCurrencySig4(data.size, 'USD')}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'rgba(180,200,220,0.9)' }}>
                          {COLOR_METRIC_OPTIONS.find((m) => m.key === treemapColorMetric)?.label}: {data.metricDisplayValue}
                        </Typography>
                      </Box>
                    );
                  }}
                />
              </Treemap>
            </ResponsiveContainer>
            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">Lowest</Typography>
              <Box
                sx={{
                  flex: 1,
                  maxWidth: 200,
                  height: 10,
                  borderRadius: 1,
                  background: 'linear-gradient(to right, hsl(0,80%,20%), #111, hsl(120,70%,25%))',
                }}
              />
              <Typography variant="caption" color="text.secondary">Highest</Typography>
            </Box>
          </Paper>}

          {subTab === 2 && <Grid container spacing={3} sx={COMPARISON_SECTION_SX}>
            {/* Revenue Comparison */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, lg: 2.5 }, height: 470 }}>
                <Typography variant="h6" gutterBottom>Revenue Comparison (USD)</Typography>
                <ResponsiveContainer width="100%" height="85%" style={{ paddingLeft: '7px' }}>
                  <BarChart data={prepareChartData('revenue')}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fill: theme.palette.text.secondary }} />
                    <YAxis 
                      tickFormatter={(value) => formatCurrency(value, 'USD')} 
                      domain={calculateYAxisDomain(prepareChartData('revenue'), 0.15)} // 15% padding
                      width={80} // Add width to ensure $ is visible
                      includeHidden={true} // Handle hidden negative values
                    />
                    <RechartsTooltip formatter={(value) => formatCurrency(value, 'USD')} {...sharedChartTooltipProps} />
                    <Bar dataKey="value" name="Revenue">
                      {prepareChartData('revenue').map((entry, index) => (
                        <Cell 
                          key={`revenue-cell-${index}`} 
                          fill={entry.value < 0 ? "#ef4444" : "#8884d8"} 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            
            {/* Market Cap Comparison */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, lg: 2.5 }, height: 470 }}>
                <Typography variant="h6" gutterBottom>Market Cap Comparison (USD)</Typography>
                <ResponsiveContainer width="100%" height="85%" style={{ paddingLeft: '7px' }}>
                  <BarChart data={prepareMarketCapChartData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fill: theme.palette.text.secondary }} />
                    <YAxis
                      tickFormatter={(value) => formatCurrency(value, 'USD')}
                      domain={calculateYAxisDomain(prepareMarketCapChartData(), 0.15)}
                      width={80}
                    />
                    <RechartsTooltip formatter={(value) => formatCurrency(value, 'USD')} {...sharedChartTooltipProps} />
                    <Bar dataKey="value" name="Market Cap">
                      {prepareMarketCapChartData().map((entry, index) => (
                        <Cell
                          key={`mktcap-cell-${index}`}
                          fill={entry.value <= 0 ? "#ef4444" : "#00bcd4"}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            
            {/* Net Income Comparison */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, lg: 2.5 }, height: 470 }}>
                <Typography variant="h6" gutterBottom>Net Income Comparison (USD)</Typography>
                <ResponsiveContainer width="100%" height="85%" style={{ paddingLeft: '7px' }}>
                  <BarChart data={prepareChartData('earnings')}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fill: theme.palette.text.secondary }} />
                    <YAxis 
                      tickFormatter={(value) => formatCurrency(value, 'USD')} 
                      domain={calculateYAxisDomain(prepareChartData('earnings'), 0.15)} // 15% padding
                      width={80} // Add width to ensure $ is visible
                      includeHidden={true}
                    />
                    <RechartsTooltip formatter={(value) => formatCurrency(value, 'USD')} {...sharedChartTooltipProps} />
                    <Bar dataKey="value" name="Net Income">
                      {prepareChartData('earnings').map((entry, index) => (
                        <Cell 
                          key={`earnings-cell-${index}`} 
                          fill={entry.value < 0 ? "#ef4444" : "#9c27b0"} 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            
            {/* Free Cash Flow Comparison */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, lg: 2.5 }, height: 470 }}>
                <Typography variant="h6" gutterBottom>Free Cash Flow Comparison (USD)</Typography>
                <ResponsiveContainer width="100%" height="85%" style={{ paddingLeft: '7px' }}>
                  <BarChart data={prepareChartData('free_cashflow')}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fill: theme.palette.text.secondary }} />
                    <YAxis 
                      tickFormatter={(value) => formatCurrency(value, 'USD')} 
                      domain={calculateYAxisDomain(prepareChartData('free_cashflow'), 0.15)} // 15% padding
                      width={80} // Add width to ensure $ is visible
                      includeHidden={true}
                    />
                    <RechartsTooltip formatter={(value) => formatCurrency(value, 'USD')} {...sharedChartTooltipProps} />
                    <Bar dataKey="value" name="Free Cash Flow">
                      {prepareChartData('free_cashflow').map((entry, index) => (
                        <Cell 
                          key={`free-cashflow-cell-${index}`} 
                          fill={entry.value < 0 ? "#ef4444" : "#3f51b5"} 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* CapEx as % of Free Cash Flow */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, lg: 2.5 }, height: 470 }}>
                <Typography variant="h6" gutterBottom>
                  CapEx as % of Free Cash Flow
                  <Tooltip title="abs(CapEx) / abs(Free Cash Flow). Values above 100% indicate CapEx exceeds free cash flow.">
                    <InfoOutlinedIcon sx={{ fontSize: 16, ml: 1, verticalAlign: 'middle', color: 'text.secondary' }} />
                  </Tooltip>
                </Typography>
                <ResponsiveContainer width="100%" height="85%" style={{ paddingLeft: '7px' }}>
                  <BarChart data={prepareCapexPctOfFcfChartData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fill: theme.palette.text.secondary }} />
                    <YAxis
                      tickFormatter={(value) => `${value.toFixed(0)}%`}
                      domain={calculateYAxisDomain(prepareCapexPctOfFcfChartData(), 0.15)}
                      width={60}
                    />
                    <RechartsTooltip
                      formatter={(value, _name, props) => props.payload.hasValue ? `${Number(value).toFixed(1)}%` : 'N/A'}
                      {...sharedChartTooltipProps}
                    />
                    <Bar dataKey="value" name="CapEx % of FCF">
                      {prepareCapexPctOfFcfChartData().map((entry, index) => (
                        <Cell
                          key={`capex-fcf-cell-${index}`}
                          fill={!entry.hasValue ? '#666' : entry.value > 100 ? '#ef4444' : entry.value > 70 ? '#ff9800' : '#4caf50'}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* Operating Income Comparison */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, lg: 2.5 }, height: 470 }}>
                <Typography variant="h6" gutterBottom>Operating Income Comparison (USD)</Typography>
                <ResponsiveContainer width="100%" height="85%" style={{ paddingLeft: '7px' }}>
                  <BarChart data={prepareChartData('operating_income')}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fill: theme.palette.text.secondary }} />
                    <YAxis 
                      tickFormatter={(value) => formatCurrency(value, 'USD')} 
                      domain={calculateYAxisDomain(prepareChartData('operating_income'), 0.15)}
                      width={80}
                      includeHidden={true}
                    />
                    <RechartsTooltip formatter={(value) => formatCurrency(value, 'USD')} {...sharedChartTooltipProps} />
                    <Bar dataKey="value" name="Operating Income">
                      {prepareChartData('operating_income').map((entry, index) => (
                        <Cell 
                          key={`operating-income-cell-${index}`} 
                          fill={entry.value < 0 ? "#ef4444" : "#ff9800"} 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* P/E Ratio Comparison (TTM) */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, lg: 2.5 }, height: 470 }}>
                <Typography variant="h6" gutterBottom>
                  P/E Ratio Comparison (TTM)
                  <Tooltip title="Price-to-Earnings = Market Cap / TTM Operating Income (trailing 4 quarters). Lower may indicate undervaluation.">
                    <InfoOutlinedIcon sx={{ fontSize: 16, ml: 1, verticalAlign: 'middle', color: 'text.secondary' }} />
                  </Tooltip>
                </Typography>
                <ResponsiveContainer width="100%" height="85%" style={{ paddingLeft: '7px' }}>
                  <BarChart data={preparePERatioChartData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fill: theme.palette.text.secondary }} />
                    <YAxis 
                      tickFormatter={(value) => `${value.toFixed(0)}x`} 
                      domain={calculateYAxisDomain(preparePERatioChartData(), 0.15)}
                      width={50}
                    />
                    <RechartsTooltip formatter={(value, name, props) => props.payload.hasOperatingIncome ? `${value.toFixed(1)}x` : 'N/A (no TTM op. income)'} {...sharedChartTooltipProps} />
                    <Bar dataKey="value" name="P/E Ratio (TTM)">
                      {preparePERatioChartData().map((entry, index) => (
                        <Cell 
                          key={`pe-ratio-cell-${index}`} 
                          fill={!entry.hasOperatingIncome ? "#666" : entry.value > 100 ? "#ef4444" : entry.value < 15 ? "#4caf50" : entry.value > 30 ? "#ff9800" : "#2196f3"} 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>}

          {subTab === 1 && (
            <Paper elevation={2} sx={{ ...COMPARISON_SECTION_SX, p: { xs: 1.5, sm: 2, lg: 2.5 }, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6">Company Metric Trends</Typography>
                  {isRefreshingComparison && (
                    <Chip
                      size="small"
                      label="Updating..."
                      icon={<CircularProgress size={12} />}
                      variant="outlined"
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <Select value={trendMetric} onChange={(event) => setTrendMetric(event.target.value)}>
                      {TREND_METRIC_OPTIONS.map((option) => (
                        <MenuItem key={option.key} value={option.key}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    color="primary"
                    value={trendYears}
                    onChange={(_event, nextValue) => { if (nextValue) setTrendYears(nextValue); }}
                  >
                    {TREND_LOOKBACK_OPTIONS.map((option) => (
                      <ToggleButton key={option.value} value={option.value}>{option.label}</ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<InfoOutlinedIcon fontSize="small" />}
                  label={trendMetricConfig.label}
                />
                <Tooltip title={`${trendMetricDetail.brief} Formula: ${trendMetricDetail.formula}`}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ cursor: 'help', textDecoration: 'underline dotted' }}
                  >
                    What is this metric?
                  </Typography>
                </Tooltip>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={trendMode}
                  onChange={(_event, nextValue) => { if (nextValue) setTrendMode(nextValue); }}
                >
                  <ToggleButton value="normalized">Indexed to 100</ToggleButton>
                  <ToggleButton value="absolute">Absolute values</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {trendMode === 'normalized'
                  ? 'Each company series starts at 100 for direct relative comparison.'
                  : 'Actual reported values for each company.'}
              </Typography>

              {trendMetric === 'price' && priceHistoryError && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>{priceHistoryError}</Alert>
              )}

              {isTrendMetricLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                  <CircularProgress size={22} />
                  <Typography variant="body2" sx={{ ml: 1.5 }}>
                    Loading {trendMetric === 'price' ? 'price history' : trendMetric === 'cash' ? 'cash' : 'financial'} points...
                  </Typography>
                </Box>
              ) : metricTrendChartData.length === 0 ? (
                <Alert severity="info">No trend data available for the selected metric and lookback.</Alert>
              ) : (
                <Box sx={{ height: { xs: 420, sm: 640, lg: 760 } }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={displayedMetricTrendChartData} margin={{ top: 8, right: 92, left: 0, bottom: 28 }}>
                      <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                        minTickGap={30}
                        tickFormatter={(value) => formatTrendDate(value)}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                        tickFormatter={(value) => {
                          if (trendMode === 'normalized') return Number(value).toFixed(0);
                          if (trendMetricConfig.isPercent) return `${Number(value).toFixed(0)}%`;
                          if (trendMetric === 'pe_ratio') return `${Number(value).toFixed(1)}x`;
                          return formatCurrency(value, 'USD');
                        }}
                        domain={['auto', 'auto']}
                        width={90}
                      />
                      <RechartsTooltip
                        labelFormatter={(label) => formatTrendDate(label, true)}
                        itemSorter={(item) => {
                          const value = Number(item?.value);
                          return Number.isFinite(value) ? -value : Number.POSITIVE_INFINITY;
                        }}
                        formatter={(value) => {
                          if (trendMode === 'normalized') return Number(value).toFixed(2);
                          if (trendMetricConfig.isPercent) return `${Number(value).toFixed(2)}%`;
                          if (trendMetric === 'pe_ratio') return `${Number(value).toFixed(2)}x`;
                          return formatCurrency(Number(value), 'USD');
                        }}
                        {...sharedChartTooltipProps}
                      />
                      <Legend
                        verticalAlign="bottom"
                        align="center"
                        iconType="line"
                        wrapperStyle={{ paddingTop: 16, color: theme.palette.text.secondary }}
                      />
                      {comparisonTickers.map((ticker, index) => (
                        <Line
                          key={`metric-trend-${ticker}`}
                          type="monotone"
                          dataKey={ticker}
                          name={ticker}
                          stroke={TREND_SERIES_COLORS[index % TREND_SERIES_COLORS.length]}
                          strokeWidth={2}
                          dot={(dotProps) => {
                            const { cx, cy, index: pointIndex, value } = dotProps;
                            if (!Number.isFinite(cx) || !Number.isFinite(cy) || !Number.isFinite(value)) return null;

                            const seriesColor = TREND_SERIES_COLORS[index % TREND_SERIES_COLORS.length];
                            const isLastPoint = pointIndex === trendSeriesLastPointIndex[ticker];

                            return (
                              <g>
                                <circle cx={cx} cy={cy} r={3} fill={seriesColor} stroke={seriesColor} strokeWidth={0} />
                                {isLastPoint && (
                                  <text
                                    x={cx + 8}
                                    y={cy + 4}
                                    fill={seriesColor}
                                    fontSize={11}
                                    fontWeight={700}
                                  >
                                    {ticker}
                                  </text>
                                )}
                              </g>
                            );
                          }}
                          activeDot={{ r: 5 }}
                          connectNulls
                          isAnimationActive={false}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Paper>
          )}

        </>
      ) : error ? (
        <Paper elevation={2} sx={{ p: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
          <Typography variant="body1">{error}</Typography>
        </Paper>
      ) : (
        <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1">
            Add companies to compare their financial metrics.
          </Typography>
        </Paper>
      )}
      {comparisonControlsPanel}
    </Box>
  );
};

export default CompanyComparison;
